import { useEffect } from 'react';
import { Link as RouterLink } from 'react-router';
import Menu from '@mui/icons-material/Menu';
// material
import { alpha, styled } from '@mui/material/styles';
import { Box, Stack, AppBar, Toolbar, IconButton } from '@mui/material';
// components
import { useDispatch, useSelector } from '../../redux/store';
import type { RootState } from '../../redux/store';
import { getNotifications } from '../../redux/slices/user';
import useAuth from '../../hooks/useAuth';
import useLocales from '../../hooks/useLocales';
import { PATH_DASHBOARD, PATH_ORG } from '../../routes/paths';
import Logo from '../../components/Logo';
import AccountPopover from './AccountPopover';
import NotificationsPopover from './NotificationsPopover';

const DRAWER_WIDTH = 260;
const APPBAR_HEIGHT = 64;

const RootStyle = styled(AppBar)(({ theme }) => ({
  boxShadow: 'none',
  backdropFilter: 'blur(6px)',
  WebkitBackdropFilter: 'blur(6px)', // Fix on Mobile
  backgroundColor: alpha(theme.palette.background.default, 0.9),
  zIndex: theme.zIndex.drawer + 1
}));

const ToolbarStyle = styled(Toolbar)(({ theme }) => ({
  minHeight: APPBAR_HEIGHT,
  paddingInline: theme.spacing(1.5),
  [theme.breakpoints.up('lg')]: {
    minHeight: APPBAR_HEIGHT,
    paddingInline: theme.spacing(2.5)
  }
}));

type DashboardNavbarProps = {
  onOpenSidebar: VoidFunction;
  isOpenSidebar?: boolean;
};

export default function DashboardNavbar({
  onOpenSidebar,
  isOpenSidebar = false
}: DashboardNavbarProps) {
  const dispatch = useDispatch();
  const { translate } = useLocales();
  const { isAuthenticated } = useAuth();
  const activeOrgGuid = useSelector((state: RootState) => state.organizationContext.activeOrgGuid);
  const logoPath = !isAuthenticated
    ? '/'
    : activeOrgGuid
      ? PATH_ORG.overview
      : PATH_DASHBOARD.overview.root;

  useEffect(() => {
    dispatch(getNotifications());
  }, [dispatch]);

  return (
    <RootStyle>
      <ToolbarStyle>
        <Box
          sx={{
            width: { xs: 'auto', lg: DRAWER_WIDTH },
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <Box
            component={RouterLink}
            to={logoPath}
            aria-label="Worknity"
            sx={{
              display: { xs: 'none', lg: 'inline-flex' },
              alignItems: 'center',
              flexShrink: 0
            }}
          >
            <Logo
              sx={{
                width: { xs: 90, sm: 114 },
                height: { xs: 27, sm: 34 },
                '& svg': { width: '100%', height: 'auto' }
              }}
            />
          </Box>
          <IconButton
            onClick={onOpenSidebar}
            aria-label={translate(isOpenSidebar ? 'menu.close_sidebar' : 'menu.open_sidebar')}
            aria-expanded={isOpenSidebar}
            sx={{ color: 'text.secondary', display: { lg: 'none' } }}
          >
            <Menu />
          </IconButton>
        </Box>

        {/* <Searchbar /> */}
        <Box sx={{ flexGrow: 1 }} />

        <Stack direction="row" spacing={{ xs: 0.5, sm: 1.5 }} sx={{ alignItems: 'center' }}>
          <NotificationsPopover />
          <AccountPopover />
        </Stack>
      </ToolbarStyle>
    </RootStyle>
  );
}
